import dayjs from 'dayjs';
import React, { memo, useEffect, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';

import CheckIcon from '@mui/icons-material/Check';
import RemoveIcon from '@mui/icons-material/Remove';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

import PrintQuote from './PrintQuote';
import PrintPaymentConfirmation from './PrintPaymentConfirmation';
import DialogConfirm from 'src/components/quotations/DialogConfirm';
import PrintConsentForm from './PrintConsentForm';
import PrintConsentForm2 from './PrintConsentForm2';
import PrintInscription from './PrintInscription';
import PrintMonitorAgreement from './PrintMonitorAgreement';
import PrintReceipt from './PrintReceipt';
import Record from './tableDetail/Record';

import { formatNumber, getField } from 'src/common/helper';
import { FieldTableType } from 'src/components/common/table/TypeCustom';
import { useNotify } from 'src/context/ContextNotify';
import { getAPI, postAPI } from 'src/utils/api';
import { getToken } from 'src/utils/auth';
import DialogInscription from './DialogInscription';
import DialogReceipt from './DialogReceipt';
import { checkHifuOrDensity, repairDataTable } from './QuotationService';
import { QuoteFullPrice, QuotePartialPrice, QuoteRegularPrice } from 'src/common/keyText';
import { PRICE_TYPES } from 'src/common/constants';

const inputSX = {
  width: '200px',
  height: '48px',
};

const titleFont = {
  fontSize: '12px',
  lineHeight: '14.4px',
  fontWeight: 400,
  color: '#00000080',
};

const contentFont = {
  fontSize: '14px',
  lineHeight: '16.7px',
  fontWeight: 500,
  color: '#484848',
};

const sizeSkeleton = {
  width: '100px',
};

const BoxColumn1 = styled(Box)({
  width: '30%',
});

const BoxColumn2 = styled(Box)({
  width: '30%',
});

const BoxColumn3 = styled(Box)({
  width: '80px',
});

const BoxHalf = styled(Box)({
  width: '50%',
});

const listStatusActive = [1, 4];

const listStatus = [
  {
    id: 1,
    status: false,
    text: '新規',
  },
  {
    id: 4,
    status: true,
    text: 'アーカイブ',
  },
];

export const optionBtnPrint = [
  {
    id: 1,
    name: '見積書',
    component: PrintQuote,
  },
  {
    id: 2,
    name: '同意書',
    component: PrintConsentForm,
  },
  // {
  //   id: 3,
  //   name: 'ウルトラフォーマー・デンシティ同意書',
  //   component: PrintConsentForm2,
  // },
  {
    id: 4,
    name: 'モニター規約書',
    component: PrintMonitorAgreement,
  },
  {
    id: 5,
    name: '表書き',
    component: PrintInscription,
  },
  {
    id: 6,
    name: '領収書',
    component: PrintReceipt,
  },
  {
    id: 7,
    name: '支払い確認書',
    component: PrintPaymentConfirmation,
  },
];

const fieldDefault: FieldTableType[] = [
  {
    label: '施術名',
    field: 'treatment',
    align: 'left',
    width: '25%',
  },
  {
    label: '',
    field: 'note',
    align: 'left',
    width: 'auto',
  },
  {
    label: '数量',
    field: 'quantity',
    align: 'left',
    width: '10%',
  },
  {
    label: QuoteRegularPrice,
    field: 'price',
    align: 'left',
    width: '20%',
    isPrice: true,
  },
];

const checkDefault = [
  { id: PRICE_TYPES.REGULAR, name: QuoteRegularPrice }
]

export type DataType = {
  patientId: number;
  id: number;
};

interface DialogDetailProps {
  data: DataType;
  screenPrint?: number;
  setPatientSelect: (value: number | null) => void;
  setFullData: (value: boolean) => void;
}

const QuotationContent: React.FC<DialogDetailProps> = (props) => {
  const notifyContext = useNotify();
  const refFontSizeScale = useRef(null);

  const [dataTable, setDataTable] = useState<any[]>([]);
  const [detailData, setDetailData] = useState<any>();
  const [loading, setLoading] = useState<boolean>(false);
  const [parts, setParts] = useState<any[]>([]);
  const [items, setItems] = useState<any[]>([]);
  const [patientInfo, setPatientInfo] = useState<any>({});
  const [showDialogConfirm, setShowDialogConfirm] = useState<boolean>(false);
  const [showDialogReceipt, setShowDialogReceipt] = useState<boolean>(false);
  const [receiptInfo, setReceiptInfo] = useState<any>({});
  const [showDialogInscription, setShowDialogInscription] = useState<boolean>(false);
  const [inscriptionInfo, setInscriptionInfo] = useState<any>({
    fontSizeScaleSurgery: 48, 
    fontSizeScaleAnesthesia: 48
  });
  const [isHifuOrDensity, setIsHifuOrDensity] = useState<boolean>(false);
  const [checkSelected, setCheckSelected] = React.useState<any[]>(checkDefault);
  const [fields, setFields] = useState<FieldTableType[]>(fieldDefault);
  const [quoteTotal, setQuoteTotal] = useState(0);

  const getBaseData = async () => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      getAPI(`/reserve/base-data?token=${token}`)
        .then((res: any) => {
          if (res.code === 200 && res.data.parts) {
            const parts = res.data.parts;
            setParts(parts);
          }
        })
        .finally(() => {});
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const setSeclectedPrice = (displayPriceType: number[]) => {
    let result = [...checkDefault];
    if (displayPriceType.includes(PRICE_TYPES.PART)) {
      result.push({
        id: PRICE_TYPES.PART,
        name: QuotePartialPrice
      })
    }
    if (displayPriceType.includes(PRICE_TYPES.FULL)) {
      result.push({
        id: PRICE_TYPES.FULL,
        name: QuoteFullPrice
      })
    }
    setCheckSelected(result)
    getFields(result)
  }

  const getQuoteDetail = () => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      setLoading(true);
      getAPI(
        `/patient/quote-view?patientid=${props.data.patientId}&id=${props.data.id}&token=${token}`
      )
        .then((res: any) => {
          if (res.code === 200 && res.data) {
            setDetailData(res.data);
            setItems(res.data.items);
            const quoteData = repairDataTable(res.data);
            setQuoteTotal(quoteData.quoteTotal || 0);
            setDataTable(quoteData.dataRepair);
            setReceiptInfo({
              patientName: getField(res.data, 'patientname', ''),
              note: '施術代として',
              receiptDate: dayjs(
                getField(res.data, 'reserve_date', new Date())
              ).format('YYYY年MM月DD日'),
              totalAmount: quoteData.totalAmount,
              totalAmountTax10: quoteData.totalAmountTax10,
              totalAmountTax8: quoteData.totalAmountTax8,
            });
            setIsHifuOrDensity(checkHifuOrDensity(res.data));
            props.setPatientSelect(getField(res.data, 'patient_select', null));
            props.setFullData(true);
            setSeclectedPrice(getField(res.data, 'display_price_type', [1]))
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const getQuoteView = () => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      setLoading(true);
      getAPI(`/patient/view?patientid=${props.data.patientId}&token=${token}`)
        .then((res: any) => {
          if (res.code === 200 && res.data) {
            const patient = getField(res.data, 'patient', {});
            setPatientInfo({
              age: getField(patient, 'age', '　　'),
              vip: getField(patient, 'vip', false),
            });
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  let currentPrintRef = useRef(null);
  const printRefQuote = useRef(null);
  const printRefConsent = useRef(null);
  const printRefConsent2 = useRef(null);
  const printRefMonitorAgreement = useRef(null);
  const printRefInscription = useRef(null);
  const printRefReceipt = useRef(null);
  const printRefPaymentConfirmation = useRef(null);
  

  const showPrintPage = useReactToPrint({
    content: () => currentPrintRef.current,
  });

  const handlePrint = (id: number) => {
    switch (id) {
      case 1:
        currentPrintRef = printRefQuote;
        showPrintPage();
        break;
      case 2:
        if (isHifuOrDensity) {
          currentPrintRef = printRefConsent2;
          showPrintPage();
        } else {
          currentPrintRef = printRefConsent;
          showPrintPage();
        }
        break;
      case 3:
        currentPrintRef = printRefConsent2;
        showPrintPage();
        break;
      case 4:
        currentPrintRef = printRefMonitorAgreement;
        showPrintPage();
        break;
      case 5:
        // setTimeout(() => {
        //   currentPrintRef = printRefInscription;
        //   showPrintPage();
        // }, 1000);
        setShowDialogInscription(true);
        break;
      case 6:
        // currentPrintRef = printRefReceipt;
        // showPrintPage();
        setShowDialogReceipt(true);
        break;
      case 7:
        currentPrintRef = printRefPaymentConfirmation;
        showPrintPage();
        break;
      default:
        break;
    }
  };

  const getFieldDetailData = (name: string, defaultVal?: string) => {
    return detailData && (detailData[name] || detailData[name] === 0)
      ? detailData[name]
      : defaultVal
      ? defaultVal
      : '-';
  };

  const formatDate = (date: string) => {
    return date && date !== '-' ? dayjs(date).format('YYYY/MM/DD') : '-';
  };

  const quoteSetPatientSelect = (patientSelect: number, newSelected: any[]) => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      const data = {
        id: getField(detailData, 'id', null),
        patient_select: patientSelect,
        patientid: getField(detailData, 'patientid', null),
        display_price_type: newSelected.map((item) => item.id)
      };

      postAPI(`/patient/quote-set-patient-select?&token=${token}`, data)
        .then((res: any) => {
          if (res.code === 200) {
            setDetailData((prevState: any) => ({
              ...prevState,
              patient_select: patientSelect,
            }));
            getQuoteDetail();
          }
          if (res.code === 501 || res.code === 500) {
            notifyContext.setNotifyMessage({
              show: true,
              message: res.msg,
              type: 'error',
            });
          }
        })
        .finally(() => {});
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  }

  const handleChangeDropDown = (event: SelectChangeEvent<string | number>) => {
    const patientSelect = event.target.value ? Number(event.target.value) : PRICE_TYPES.REGULAR;
    quoteSetPatientSelect(patientSelect, checkSelected)
  };

  const handleChangeSwitch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;

    if (checked) {
      setShowDialogConfirm(true);
    } else {
      callAPIArchive(false);
    }
  };

  const handleCloseConfirm = () => {
    setShowDialogConfirm(false);
  };

  const callAPIArchive = (status = true) => {
    const statusFind = listStatus.find((item) => item.status === status);

    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      const data = {
        id: getField(detailData, 'id', null),
        status: statusFind ? statusFind.status : false,
        patientid: getField(detailData, 'patientid', null),
      };

      postAPI(`/patient/quote-archive?&token=${token}`, data)
        .then((res: any) => {
          handleCloseConfirm();
          if (res.code === 200) {
            setDetailData((prevState: any) => ({
              ...prevState,
              status: statusFind ? statusFind.id : 1,
              statustext: statusFind ? statusFind.text : '',
            }));
          }
          if (res.code === 501 || res.code === 500) {
            notifyContext.setNotifyMessage({
              show: true,
              message: res.msg,
              type: 'error',
            });
          }
        })
        .finally(() => {});
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (props.data.id && props.data.patientId) {
      getQuoteDetail();
      getBaseData();
      getQuoteView();
    }
    // eslint-disable-next-line
  }, [props.data]);

  useEffect(() => {
    if (props.screenPrint) {
      handlePrint(props.screenPrint);
    }

    if (props.screenPrint === 3) {
      let boxContent: any = refFontSizeScale.current;

      if (boxContent) {
        // const divHeight = boxContent.clientHeight;
        // const scrollHeight = boxContent.scrollHeight;
        // const style = window.getComputedStyle(boxContent);
        // let fontSize = parseFloat(style.fontSize);
        // const percent = scrollHeight / divHeight;
        // if (percent > 1) {
        //   fontSize = Math.floor(fontSize / percent - 2);
        // }
        // fontSize = fontSize < 1 ? 1 : fontSize;
        // setFontSizeScale(fontSize);
      }
    }

    // eslint-disable-next-line
  }, [props.screenPrint]);

  const handleCloseDialogReceipt = () => {
    setShowDialogReceipt(false);
  };

  const handleConfirmDialogReceipt = () => {
    setShowDialogReceipt(false);

    currentPrintRef = printRefReceipt;
    showPrintPage();
  };

  const handleChangeDataDialogReceipt = (name: string, value: string) => {
    setReceiptInfo((prevState: any) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleCloseDialogInscription = () => {
    setShowDialogInscription(false);
  };

  const handleConfirmDialogInscription = () => {
    setShowDialogInscription(false);

    currentPrintRef = printRefInscription;
    showPrintPage();
  };

  const handleChangeDataDialogInscription = (name: string, value: number) => {
    setInscriptionInfo((prevState: any) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const getFields = (newSelected: any[]) => {
    let fieldTemp = [...fieldDefault];

    newSelected.map((select) => {
      if (select.name === QuotePartialPrice) {
        const partField: FieldTableType = {
          label: QuotePartialPrice,
          field: 'pricePartial',
          align: 'left',
          width: '20%',
          isPrice: true,
        };
        fieldTemp.push(partField);
      }

      if (select.name === QuoteFullPrice) {
        const fullField: FieldTableType = {
          label: QuoteFullPrice,
          field: 'priceFullFace',
          align: 'left',
          width: '20%',
          isPrice: true,
        };
        fieldTemp.push(fullField);
      }
      return select;
    });

    setFields(fieldTemp);

  }

  return (
    <Box>
      <Stack spacing='15px'>
        <Divider />

        <Box display={'flex'} justifyContent={'space-between'}>
          <BoxColumn1>
            <Typography sx={{ ...titleFont }}>見積番号</Typography>
            {loading ? (
              <Skeleton animation='wave' sx={sizeSkeleton} />
            ) : (
              <Typography sx={{ ...contentFont }}>
                {getFieldDetailData('id')}
              </Typography>
            )}
          </BoxColumn1>
          <BoxColumn2>
            <Typography sx={{ ...titleFont }}>タイトル</Typography>
            {loading ? (
              <Skeleton animation='wave' sx={sizeSkeleton} />
            ) : (
              <Typography sx={{ ...contentFont }}>
                {getFieldDetailData('title', '-')}
              </Typography>
            )}
          </BoxColumn2>
          <BoxColumn3>
            <Typography sx={{ ...titleFont }}>見積タイプ</Typography>
            {loading ? (
              <Skeleton animation='wave' sx={sizeSkeleton} />
            ) : (
              <Typography sx={{ ...contentFont }}>
                {getFieldDetailData('type')}
              </Typography>
            )}
          </BoxColumn3>
        </Box>

        <Box display={'flex'} justifyContent={'space-between'}>
          <BoxColumn1>
            <Typography
              sx={{ ...titleFont, mr: '5px', alignContent: 'center' }}
            >
              発行日
            </Typography>
            {loading ? (
              <Skeleton animation='wave' sx={sizeSkeleton} />
            ) : (
              <Typography sx={{ ...contentFont }}>
                {formatDate(getField(detailData, 'quotedate', '-'))}
              </Typography>
            )}
          </BoxColumn1>
          <BoxColumn3>
            <Typography
              sx={{ ...titleFont, mr: '5px', alignContent: 'center' }}
            >
              実施日
            </Typography>
            {loading ? (
              <Skeleton animation='wave' sx={sizeSkeleton} />
            ) : (
              <Typography sx={{ ...contentFont }}>
                {formatDate(getField(detailData, 'reserve_date', '-'))}
              </Typography>
            )}
          </BoxColumn3>
        </Box>

        <BoxColumn1>
          <Typography sx={{ ...titleFont }}>患者名</Typography>
          {loading ? (
            <Skeleton animation='wave' sx={sizeSkeleton} />
          ) : (
            <Typography sx={{ ...contentFont }}>
              {getField(detailData, 'patientname', '') +
                ` (${getField(detailData, 'patientno', '')})`}
            </Typography>
          )}
        </BoxColumn1>

        <Box display={'flex'} justifyContent={'space-between'}>
          <BoxColumn1>
            <Typography sx={{ ...titleFont }}>医師</Typography>
            {loading ? (
              <Skeleton animation='wave' sx={sizeSkeleton} />
            ) : (
              <Typography sx={{ ...contentFont }}>
                {getField(detailData, 'doctor', '-')}
              </Typography>
            )}
          </BoxColumn1>
          <BoxColumn2>
            <Typography sx={{ ...titleFont, mr: '5px' }}>担当者</Typography>
            {loading ? (
              <Skeleton animation='wave' sx={sizeSkeleton} />
            ) : (
              <Typography sx={{ ...contentFont }}>
                {getField(detailData, 'fullname', '-')}
              </Typography>
            )}
          </BoxColumn2>
          <BoxColumn3 />
        </Box>

        <Divider />

        <Box display={'flex'} justifyContent={'space-between'}>
        

          <BoxHalf>
            <Stack>
              <Typography sx={{ ...titleFont }}>表示価格タイプ</Typography>
              <Box>
                <Typography>{QuoteRegularPrice}</Typography>
                {getField(detailData, 'display_price_type', [PRICE_TYPES.REGULAR]).includes(PRICE_TYPES.PART) && <Typography>{QuotePartialPrice}</Typography>}
                {getField(detailData, 'display_price_type', [PRICE_TYPES.REGULAR]).includes(PRICE_TYPES.FULL) && <Typography>{QuoteFullPrice}</Typography>}
              </Box>
            </Stack>
          </BoxHalf>
          <BoxHalf>
            <Stack>
              <Typography sx={{ ...titleFont }}>返金</Typography>
              {loading ? (
                <Skeleton animation='wave' sx={sizeSkeleton} />
              ) : (
                <Typography sx={{ ...contentFont }}>
                  {Number(getField(detailData, 'has_refund', 0)) === 1 ? (
                    <CheckIcon width={'20px'} height={'20px'} />
                  ) : (
                    <RemoveIcon width={'20px'} height={'20px'} />
                  )}
                </Typography>
              )}
            </Stack>
            <Stack>
              <Typography sx={{ ...titleFont }}>最大施術時間</Typography>
              {loading ? (
                <Skeleton animation='wave' sx={sizeSkeleton} />
              ) : (
                <Typography sx={{ ...contentFont }}>
                  {`${getField(detailData, 'duration', 0)}時`}
                </Typography>
              )}
            </Stack>
          </BoxHalf>
        </Box>

        <Box display={'flex'} justifyContent={'space-between'}>
          <BoxHalf>
            <Typography sx={{ ...titleFont }}>ステータス</Typography>
            {loading ? (
              <Skeleton animation='wave' sx={sizeSkeleton} />
            ) : (
              <Box display={'flex'} justifyContent={'row'}>
                <Typography sx={{ ...contentFont, mt: '10px' }}>
                  {getField(detailData, 'statustext', '-')}
                </Typography>

                {listStatusActive.includes(
                  getField(detailData, 'status', 0)
                ) && (
                  <FormControlLabel
                    control={
                      <Switch
                        size='medium'
                        sx={{ mr: '7px' }}
                        checked={
                          getField(detailData, 'status', 0) === 1 ? false : true
                        }
                        name='status'
                        onChange={(event) => handleChangeSwitch(event)}
                      />
                    }
                    label=''
                    sx={{
                      ml: '0px',
                      '& .MuiTypography-root': { fontSize: '14px' },
                    }}
                  />
                )}
              </Box>
            )}
          </BoxHalf>
          <BoxHalf>
            <Typography sx={{ ...titleFont, mb: '10px' }}>患者選択</Typography>
            <Select
              labelId='demo-multiple-name-label'
              id='demo-multiple-name'
              value={getField(detailData, 'patient_select', 1)}
              onChange={handleChangeDropDown}
              sx={{ ...inputSX }}
            >
              {checkSelected.map((option, index) => (
                <MenuItem key={index} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </BoxHalf>
        </Box>

        <Divider />

        <Box>
          <Record fields={fields} loading={loading} items={dataTable} />
        </Box>

        <Box>
          <Typography sx={{ ...titleFont }}>備考</Typography>
          {loading ? (
            <Box>
              <Skeleton animation='wave' />
              <Skeleton animation='wave' />
              <Skeleton animation='wave' />
            </Box>
          ) : (
            <Typography
              sx={{ ...contentFont, fontWeight: '400', fontSize: '12px', wordBreak: 'break-word' }}
              whiteSpace={'pre-line'}
              component={'div'}
            >
              {getField(detailData, 'note', '-')}
            </Typography>
          )}
        </Box>
      </Stack>

      <Paper sx={{ mt: '10px', p: '10px' }}>
        <Box>
          <Typography sx={{ ...titleFont }}>入金予定</Typography>
          {loading ? (
            <Box>
              <Skeleton animation='wave' />
              <Skeleton animation='wave' />
              <Skeleton animation='wave' />
            </Box>
          ) : (
            <Typography
              sx={{ ...contentFont, fontWeight: '400', fontSize: '12px', wordBreak: 'break-word' }}
              whiteSpace={'pre-line'}
              component={'div'}
            >
              {getField(detailData, 'payment_note', '-')}
            </Typography>
          )}
        </Box>
      </Paper>

      <Paper sx={{ mt: '10px', p: '10px' }}>
        <Box>
          <Typography sx={{ ...titleFont }}>メモ</Typography>
          {loading ? (
            <Box>
              <Skeleton animation='wave' />
              <Skeleton animation='wave' />
              <Skeleton animation='wave' />
            </Box>
          ) : (
            <Typography
              sx={{ ...contentFont, fontWeight: '400', fontSize: '12px', wordBreak: 'break-word' }}
              whiteSpace={'pre-line'}
              component={'div'}
            >
              {getField(detailData, 'memo', '-')}
            </Typography>
          )}
        </Box>
      </Paper>

      <Paper sx={{ mt: '10px', p: '10px' }}>
        <Box>
          <Typography sx={{ ...titleFont, color: '#FC6E51' }}>未納金額</Typography>
          {loading ? (
            <Skeleton animation='wave' sx={sizeSkeleton} />
          ) : (
            <Typography sx={{ ...contentFont, color: '#FC6E51' }}>
              {formatNumber(
                quoteTotal -
                  getField(detailData, 'paymenttotal', 0)
              ) + '円'}
            </Typography>
          )}
        </Box>
      </Paper>

      <Box
        style={{
          display: 'none',
        }}
      >
        <Box ref={printRefQuote}>
          <PrintQuote detailData={detailData} selected={checkSelected}/>
        </Box>
      </Box>

      <Box
        style={{
          display: 'none',
        }}
      >
        <Box ref={printRefConsent}>
          <PrintConsentForm parts={parts} items={items} />
        </Box>
      </Box>

      <Box
        style={{
          display: 'none',
        }}
      >
        <Box ref={printRefConsent2}>
          <PrintConsentForm2 parts={parts} items={items} />
        </Box>
      </Box>

      <Box
        style={{
          display: 'none',
        }}
      >
        <Box ref={printRefMonitorAgreement}>
          <PrintMonitorAgreement
            detailData={detailData}
            parts={parts}
            items={items}
          />
        </Box>
      </Box>

      <Box
        style={{
          display: 'none',
        }}
      >
        <Box ref={printRefInscription}>
          <PrintInscription
            detailData={detailData}
            patientInfo={patientInfo}
            fontSizeScaleSurgery={inscriptionInfo.fontSizeScaleSurgery}
            fontSizeScaleAnesthesia={inscriptionInfo.fontSizeScaleAnesthesia}
          />
        </Box>
      </Box>

      <Box
        style={{
          display: 'none',
        }}
      >
        <Box ref={printRefReceipt}>
          <PrintReceipt
            detailData={detailData}
            patientInfo={patientInfo}
            receiptInfo={receiptInfo}
          />
        </Box>
      </Box>

      <Box style={{ 
        display: 'none' 
      }}>
        <Box ref={printRefPaymentConfirmation}>
          <PrintPaymentConfirmation 
            detailData={detailData} 
            amountData={{
              quoteTotal: quoteTotal,
              unpaid: quoteTotal - getField(detailData, 'paymenttotal', 0)
            }}
          />
        </Box>
      </Box>

      <Box sx={{ overflow: 'hidden' }} height={'0px'}>
        <Box
          ref={refFontSizeScale}
          height={'116.416mm'}
          sx={{
            overflowX: 'scroll',
            fontSize: '28px',
          }}
          id='box-content'
        >
          <Typography
            sx={{
              fontSize: '20px',
              fontWeight: '500',
              lineHeight: '1.5',
            }}
          >
            施術一覧
          </Typography>
          {detailData &&
            detailData['items'] &&
            detailData['items'].map((item: any, index: number) => (
              <Typography
                key={index}
                sx={{
                  fontSize: '28px',
                  fontWeight: '500',
                  lineHeight: '1.5',
                }}
              >
                {getField(item.surgery, 'surgery')}
              </Typography>
            ))}
        </Box>
      </Box>
      <DialogConfirm
        open={showDialogConfirm}
        content={'見積をアーカイブします、本当によろしいですか？'}
        onClose={handleCloseConfirm}
        onSubmit={() => callAPIArchive()}
      />
      <DialogReceipt
        open={showDialogReceipt}
        onClose={handleCloseDialogReceipt}
        onConfirm={handleConfirmDialogReceipt}
        receiptData={receiptInfo}
        handleChangeData={handleChangeDataDialogReceipt}
      />
      <DialogInscription
        open={showDialogInscription}
        onClose={handleCloseDialogInscription}
        onConfirm={handleConfirmDialogInscription}
        detailData={detailData}
        patientInfo={patientInfo}
        fontSizeScaleSurgery={inscriptionInfo.fontSizeScaleSurgery}
        fontSizeScaleAnesthesia={inscriptionInfo.fontSizeScaleAnesthesia}
        handleChangeData={handleChangeDataDialogInscription}
      />
    </Box>
  );
};

export default memo(QuotationContent);
